/**
 * MoveIndexPage — index route for /moves/:quoteId
 *
 * On mobile: shows the hero card (since left panel is hidden) + detail cards.
 * On desktop (md+): only shows detail cards (hero card is in the shell's left panel).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Box,
  Calendar,
  Camera,
  ChevronRight,
  CreditCard,
  FlaskConical,
  Footprints,
  Home,
  LayoutGrid,
  ListChecks,
  Package,
  Trash2,
  Users,
  UserCircle,
  Wrench,
  X,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { useUserProfile } from '../../context/UserProfileContext';
import { useJob } from '../../hooks/useJobs';
import { useOrgAdminUsers } from '../../hooks/useOrgAdminUsers';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useOrgWarehouses } from '../../hooks/useOrgWarehouses';
import { useOrgTrucks } from '../../hooks/useOrgTrucks';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { api } from '../../lib/api';
import { IssueLogModal, type IssueFormData } from '../../components/jobs/IssueLogModal';
import { SignOffCaptureModal, WaiveSignOffModal, SignOffDetailModal } from '../../components/jobs/SignOffModal';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { usePaymentRequest } from '../../hooks/usePaymentRequest';
import {
  LifecycleStage,
  LIFECYCLE_STAGE_LABEL,
  LeadStatus,
  QuoteStatus,
  JobStatus,
  JOB_STAGES,
  JOB_STAGE_LABEL,
  JOB_STAGE_ACTION,
  OrgUser,
  QuotePatch,
} from '../../types/admin';
import {
  HeroCard,
  BottomSheet,
  StatusSheet,
  UserPickerSheet,
  EstimateScheduledSheet,
  CommunicationLogSheet,
  RequestPaymentSheet,
  EditableCustomerCard,
  EditableCompanyCard,
  EditableLeadInfoCard,
  EditableMoveInfoCard,
  EditableAddressesCard,
  EditablePropertyDetailsCard,
  EditableNotesCard,
  getCardsForStage,
} from './MovePageComponents';

export function MoveIndexPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;

  const { quote, loading, error, updateQuote, calculateTransit, calculateWarehouseTransits, refetch } = useSharedQuote();

  // Fetch job data when the quote is in the job stage (by explicit jobStatus OR lifecycleStage)
  const jobQuoteId = (quote?.jobStatus || quote?.lifecycleStage === 'job') ? (quoteId ?? null) : null;
  const { job, logJobIssue, updateJobStatus, updateJobStage, checkOutJobTruck, checkInJobTruck, refetch: refetchJob } = useJob(orgId, jobQuoteId);

  // Start Job handler
  const [startingJob, setStartingJob] = useState(false);
  const handleStartJob = useCallback(async () => {
    if (!orgId || !quoteId) return;
    setStartingJob(true);
    try {
      await updateJobStatus('in_progress');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[start job]', err);
    } finally {
      setStartingJob(false);
    }
  }, [orgId, quoteId, updateJobStatus, refetchJob, refetch]);

  // ── Job in-progress state ───────────────────────────────────────────────
  const [advancing, setAdvancing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);

  // Sign-off state
  const [showSignOffCapture, setShowSignOffCapture] = useState(false);
  const [showWaiveSignOff, setShowWaiveSignOff] = useState(false);
  const [showSignOffDetail, setShowSignOffDetail] = useState(false);
  const [signOffSubmitting, setSignOffSubmitting] = useState(false);
  const [signOffError, setSignOffError] = useState<string | null>(null);

  // Payment request state
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const { sending: paymentSending, sendRequest: sendPaymentRequest } = usePaymentRequest(orgId, quoteId ?? null);

  // ── Stage advance/revert handlers ───────────────────────────────────────
  const handleAdvance = useCallback(async () => {
    setConfirmAdvance(false);
    setAdvancing(true);
    try {
      await updateJobStage('advance');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[advance stage]', err);
    } finally {
      setAdvancing(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  const handleRevert = useCallback(async () => {
    setConfirmRevert(false);
    setReverting(true);
    try {
      await updateJobStage('revert');
      await refetchJob();
      await refetch();
    } catch (err) {
      console.error('[revert stage]', err);
    } finally {
      setReverting(false);
    }
  }, [updateJobStage, refetchJob, refetch]);

  // ── Sign-off handlers ──────────────────────────────────────────────────
  const handleRequestSignOff = useCallback(() => {
    setShowSignOffCapture(true);
  }, []);

  const handleSignOffCapture = useCallback(async (signerName: string, signatureDataUrl: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    setSignOffError(null);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'capture',
        signerName,
        signatureImage: signatureDataUrl,
      });
      setShowSignOffCapture(false);
      setSignOffError(null);
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'customer_sign_off') {
        try { await updateJobStage('advance'); } catch (err) { console.error('[signoff advance]', err); }
      }
      await refetchJob();
      await refetch();
      // Open payment modal if there's an outstanding balance
      if ((job?.paymentSummary?.outstandingAmountCents ?? 0) > 0) {
        setShowPaymentSheet(true);
      }
    } catch (err) {
      console.error('[signoff capture]', err);
      setSignOffError(err instanceof Error ? err.message : 'Failed to save sign-off. Please try again.');
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, job?.paymentSummary?.outstandingAmountCents, updateJobStage, refetchJob, refetch]);

  const handleSignOffWaive = useCallback(async (reason: string) => {
    if (!orgId || !quoteId) return;
    setSignOffSubmitting(true);
    try {
      await api.patch(`/jobs/${orgId}/${quoteId}/signoff`, {
        action: 'waive',
        waiveReason: reason,
      });
      setShowWaiveSignOff(false);
      const currentStage = job?.jobStage ?? 'scheduled';
      if (currentStage === 'customer_sign_off') {
        await updateJobStage('advance');
      }
      await refetchJob();
      await refetch();
      // Open payment modal if there's an outstanding balance
      if ((job?.paymentSummary?.outstandingAmountCents ?? 0) > 0) {
        setShowPaymentSheet(true);
      }
    } catch (err) {
      console.error('[signoff waive]', err);
    }
    setSignOffSubmitting(false);
  }, [orgId, quoteId, job?.jobStage, job?.paymentSummary?.outstandingAmountCents, updateJobStage, refetchJob, refetch]);

  // ── Payment request handler ─────────────────────────────────────────────
  const handleSendPaymentRequest = useCallback(async (channels: ('sms' | 'email')[]) => {
    await sendPaymentRequest(channels);
    setShowPaymentSheet(false);
  }, [sendPaymentRequest]);

  const handleReconciled = useCallback(() => {
    refetchJob();
  }, [refetchJob]);

  // Fetch quote version count
  const [versionsCount, setVersionsCount] = useState(0);
  useEffect(() => {
    if (!orgId || !quoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: unknown[] }>(
          `/quotes/${orgId}/${quoteId}/versions`,
        );
        if (!cancelled) setVersionsCount(res.data.length);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId]);

  // Org users for pickers (mobile hero card)
  const { users: orgUsers, loading: usersLoading } = useOrgAdminUsers(orgId ?? '');
  const { org: orgSettings } = useOrgSettings(orgId ?? '');
  const { warehouses } = useOrgWarehouses(orgId ?? '');
  const { trucks: fleetTrucks, refetch: refetchTrucks } = useOrgTrucks(orgId ?? '');
  const warehousesRef = useRef(warehouses);
  warehousesRef.current = warehouses;

  const handleCheckOutTruck = useCallback(async (truckId: string) => {
    try {
      const result = await checkOutJobTruck(truckId);
      await refetchTrucks();
      if (result?.stageAdvanced) {
        await refetchJob();
        await refetch();
      }
    } catch (err) {
      console.error('[truck checkout]', err);
    }
  }, [checkOutJobTruck, refetchTrucks, refetchJob, refetch]);

  const handleCheckInTruck = useCallback(async (truckId: string, warehouseId: string) => {
    try {
      const result = await checkInJobTruck(truckId, warehouseId);
      await refetchTrucks();
      if (result.stageAdvanced) {
        await refetchJob();
        await refetch();
      }
      return result;
    } catch (err) {
      console.error('[truck checkin]', err);
      throw err;
    }
  }, [checkInJobTruck, refetchTrucks, refetchJob, refetch]);

  const stage: LifecycleStage = quote?.lifecycleStage ?? (quote?.jobStatus ? 'job' : quote?.leadStatus ? 'lead' : 'quote');

  // Modal state (for mobile hero card interactions)
  const [showStatusSheet, setShowStatusSheet] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [userPickerMode, setUserPickerMode] = useState<'quoteLead' | 'moveLead'>('quoteLead');
  const [showEstimateSheet, setShowEstimateSheet] = useState(false);
  const [showActivitySheet, setShowActivitySheet] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);

  // Save helper for editable cards
  const save = useCallback(async (patch: QuotePatch) => {
    try { await updateQuote(patch); } catch (err) { console.error('[move index save]', err); }
  }, [updateQuote]);

  // Transit state
  const originPlaceId = quote?.addresses?.origin?.placeId;
  const originAddress = quote?.addresses?.origin?.address;
  const destPlaceId = quote?.addresses?.destination?.placeId;
  const destAddress = quote?.addresses?.destination?.address;
  const originZip = quote?.originZipCode;
  const destZip = quote?.destinationZipCode;
  const [calculatingTransit, setCalculatingTransit] = useState(false);
  const lastTransitKeyRef = useRef('');

  // Lead transit result (stored locally since calculate-lead-transit doesn't save to quote)
  const [leadTransit, setLeadTransit] = useState<{ distanceMeters: number; durationSeconds: number } | null>(null);

  // Warehouse distance state (lead stage)
  const [warehouseDistance, setWarehouseDistance] = useState<{
    loading: boolean;
    name: string | null;
    distanceMeters: number | null;
    durationSeconds: number | null;
  }>({ loading: false, name: null, distanceMeters: null, durationSeconds: null });
  const lastWarehouseKeyRef = useRef('');

  // Transit calculation — placeId-based for quote/job stages
  useEffect(() => {
    if (stage === 'lead') return; // leads use the lead-transit endpoint below
    if (!originPlaceId || !destPlaceId) return;
    const key = `${originPlaceId}|${destPlaceId}`;
    if (key === lastTransitKeyRef.current) return;
    lastTransitKeyRef.current = key;

    setCalculatingTransit(true);
    calculateTransit(originPlaceId, destPlaceId)
      .then(() => {
        setCalculatingTransit(false);
        const wh = warehousesRef.current;
        if (wh.length) {
          calculateWarehouseTransits(
            wh.map(w => ({ id: w.id, placeId: w.placeId })),
            originPlaceId,
            destPlaceId,
          ).catch(err => console.error('[warehouse transit]', err));
        }
      })
      .catch(err => {
        lastTransitKeyRef.current = '';
        setCalculatingTransit(false);
        console.error('[transit calc]', err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, originPlaceId, destPlaceId, calculateTransit, calculateWarehouseTransits]);

  // Lead transit — supports any combo of placeId, address, or zip code
  useEffect(() => {
    if (stage !== 'lead' || !orgId) return;
    const hasOrigin = originPlaceId || originZip;
    const hasDest = destPlaceId || destZip;
    if (!hasOrigin || !hasDest) {
      setLeadTransit(null);
      return;
    }
    const key = `lead|${originPlaceId ?? ''}|${originAddress ?? ''}|${originZip ?? ''}|${destPlaceId ?? ''}|${destAddress ?? ''}|${destZip ?? ''}`;
    if (key === lastTransitKeyRef.current) return;
    lastTransitKeyRef.current = key;

    setCalculatingTransit(true);
    api.post<{ success: boolean; data: { distanceMeters: number; durationSeconds: number; moveType: 'local' | 'intrastate' | 'interstate' } }>(
      `/quotes/${orgId}/calculate-lead-transit`,
      {
        originPlaceId: originPlaceId || undefined,
        originZipCode: originZip || undefined,
        originAddress: originAddress || undefined,
        destinationPlaceId: destPlaceId || undefined,
        destinationZipCode: destZip || undefined,
        destinationAddress: destAddress || undefined,
      },
    ).then(res => {
      setLeadTransit({ distanceMeters: res.data.distanceMeters, durationSeconds: res.data.durationSeconds });
      setCalculatingTransit(false);
      // Auto-populate move type
      if (res.data.moveType) {
        updateQuote({ moveType: res.data.moveType }).catch(err => console.error('[auto move type]', err));
      }
    }).catch(err => {
      lastTransitKeyRef.current = '';
      setCalculatingTransit(false);
      console.error('[lead transit calc]', err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, orgId, originPlaceId, originAddress, originZip, destPlaceId, destAddress, destZip]);

  // Warehouse distance for leads — find nearest warehouse to origin
  useEffect(() => {
    if (stage !== 'lead' || !orgId) return;
    const hasOrigin = originPlaceId || originZip;
    if (!hasOrigin || !warehouses.length) {
      setWarehouseDistance({ loading: false, name: null, distanceMeters: null, durationSeconds: null });
      return;
    }
    const key = `wh|${originPlaceId ?? ''}|${originAddress ?? ''}|${originZip ?? ''}|${warehouses.map(w => w.id).join(',')}`;
    if (key === lastWarehouseKeyRef.current) return;
    lastWarehouseKeyRef.current = key;

    setWarehouseDistance(prev => ({ ...prev, loading: true }));

    // Calculate distance from each warehouse to origin using lead-transit endpoint
    const promises = warehouses.map(w =>
      api.post<{ success: boolean; data: { distanceMeters: number; durationSeconds: number } }>(
        `/quotes/${orgId}/calculate-lead-transit`,
        {
          originPlaceId: w.placeId,
          originAddress: w.address,
          destinationPlaceId: originPlaceId || undefined,
          destinationZipCode: originZip || undefined,
          destinationAddress: originAddress || undefined,
        },
      ).then(res => ({ id: w.id, name: w.name, ...res.data }))
        .catch(() => null),
    );

    Promise.all(promises).then(results => {
      const valid = results.filter((r): r is NonNullable<typeof r> => r !== null);
      if (!valid.length) {
        setWarehouseDistance({ loading: false, name: null, distanceMeters: null, durationSeconds: null });
        return;
      }
      // Pick nearest by duration
      let best = valid[0];
      for (const r of valid) {
        if (r.durationSeconds < best.durationSeconds) best = r;
      }
      setWarehouseDistance({
        loading: false,
        name: best.name,
        distanceMeters: best.distanceMeters,
        durationSeconds: best.durationSeconds,
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, orgId, originPlaceId, originAddress, originZip, warehouses]);

  // Status change handler (for mobile hero)
  const handleStatusChange = useCallback(async (status: string) => {
    if (!quote) return;
    if (stage === 'lead') {
      await updateQuote({ leadStatus: status as LeadStatus });
    } else if (stage === 'quote') {
      await updateQuote({ quoteStatus: status as QuoteStatus });
    } else {
      await updateQuote({ jobStatus: status as JobStatus });
      // Refetch job data so hero card and progress card update
      await refetchJob();
    }
    await refetch();
  }, [quote, stage, updateQuote, refetchJob, refetch]);

  const handleEstimateScheduled = useCallback(async (estimateDate: string, estimatorUid: string, _estimatorName: string) => {
    setShowEstimateSheet(false);
    try {
      const patch: Record<string, unknown> = {
        leadStatus: 'estimate_scheduled',
        lifecycleStage: 'quote',
        quoteStatus: 'draft',
      };
      if (estimateDate) patch.estimateVisitDate = estimateDate;
      if (estimatorUid) patch.quoteLeadUserId = estimatorUid;
      await updateQuote(patch as Parameters<typeof updateQuote>[0]);
      navigate(`/moves/${quoteId}`);
    } catch (err) {
      console.error('[estimate scheduled]', err);
    }
  }, [updateQuote, navigate, quoteId]);

  const handleLeadAssign = useCallback(async (user: OrgUser) => {
    if (userPickerMode === 'quoteLead') {
      await updateQuote({ quoteLeadUserId: user.uid });
    } else {
      await updateQuote({ moveLeadUserId: user.uid });
    }
  }, [userPickerMode, updateQuote]);

  const openLeadPicker = useCallback(() => {
    setUserPickerMode(stage === 'job' ? 'moveLead' : 'quoteLead');
    setShowUserPicker(true);
  }, [stage]);

  const currentStatus = stage === 'lead'
    ? (quote?.leadStatus ?? 'new')
    : stage === 'quote'
      ? (quote?.quoteStatus ?? 'draft')
      : (quote?.jobStatus ?? 'scheduled');

  const currentLeadUid = stage === 'job'
    ? quote?.moveLeadUserId
    : quote?.quoteLeadUserId ?? quote?.createdBy;

  const pickerUsers = stage === 'job'
    ? orgUsers.filter(u => u.status === 'active' && (u.role === 'mover' || u.role === 'admin' || u.role === 'field_lead'))
    : orgUsers.filter(u => u.status === 'active');

  // Delete state — must be before early returns to satisfy Rules of Hooks
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingQuote, setDeletingQuote] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <AlertCircle size={32} className="text-rose/50 mb-2" />
        <p className="text-small text-rose font-semibold">{error ?? 'Record not found'}</p>
        <button type="button" onClick={() => navigate(-1)}
          className="text-[13px] text-plum hover:underline mt-2">Go Back</button>
      </div>
    );
  }

  const cards = stage !== 'lead' ? getCardsForStage(quote, stage) : null;
  const transit = stage === 'lead' ? leadTransit : (quote.addresses?.transit ?? null);

  // Demo / admin flags (for formula details visibility)
  const isOrgAdmin = activeOrg?.role === 'admin' || activeOrg?.role === 'field_lead';
  const isDemo = orgSettings?.isDemo ?? false;

  async function handleDeleteQuote() {
    if (!orgId || !quote) return;
    setDeletingQuote(true);
    try {
      await api.del(`/quotes/${orgId}/${quote.id}`);
      navigate('/');
    } catch (err) {
      console.error('[delete quote]', err);
      setDeletingQuote(false);
    }
  }

  // Nav items for mobile (quote/job stages only)
  const mobileNavItems = [
    ...(stage !== 'lead' ? [
      { to: `/moves/${quoteId}/customer-lead`, icon: <UserCircle size={16} />, label: 'Customer & Lead Details' },
      // Payments is part of the Customer Payments feature; hide it unless the org has it enabled.
      ...(activeOrg?.featureCustomerPayments ? [
        { to: `/moves/${quoteId}/payments`, icon: <CreditCard size={16} />, label: 'Payments' },
      ] : []),
      ...(stage === 'job' ? [
        { to: `/moves/${quoteId}/crew-trucks`, icon: <Users size={16} />, label: 'Crew & Trucks' },
      ] : []),
      { to: `/moves/${quoteId}/dates-logistics`, icon: <Calendar size={16} />, label: 'Key Dates & Addresses' },
      ...(quote?.type === 'commercial' ? [
        { to: `/moves/${quoteId}/access-logistics`, icon: <Footprints size={16} />, label: 'Logistics' },
        { to: `/moves/${quoteId}/casegoods`, icon: <Box size={16} />, label: 'Casegoods Inventory' },
        { to: `/moves/${quoteId}/modular-workstations`, icon: <LayoutGrid size={16} />, label: 'Modular Workstations' },
        { to: `/moves/${quoteId}/materials`, icon: <Package size={16} />, label: 'Materials' },
        { to: `/moves/${quoteId}/accessorial-services`, icon: <Wrench size={16} />, label: 'Accessorial Services' },
      ] : []),
      ...(quote?.type === 'residential' ? [
        { to: `/moves/${quoteId}/access-property`, icon: <Home size={16} />, label: 'Property Details' },
        { to: `/moves/${quoteId}/residential-rooms`, icon: <Box size={16} />, label: 'Rooms & Inventory' },
        { to: `/moves/${quoteId}/materials`, icon: <Package size={16} />, label: 'Materials' },
      ] : []),
      { to: `/moves/${quoteId}/scope-of-services`, icon: <ListChecks size={16} />, label: 'Scope of Services' },
      { to: `/moves/${quoteId}/photos-notes`, icon: <Camera size={16} />, label: 'Photos & Notes' },
    ] : []),
    ...(stage !== 'lead' && isDemo && isOrgAdmin
      ? [{ to: `/moves/${quoteId}/formula`, icon: <FlaskConical size={16} />, label: 'Formula Details' }]
      : []),
    { to: `/moves/${quoteId}/history`, icon: <Activity size={16} />, label: 'History' },
  ];

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Mobile: back button + hero card (hidden on md+ where the shell left panel shows it) */}
      <div className="md:hidden">
        <button type="button" onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-3
                     transition-colors duration-150 -ml-1">
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="mb-4">
          <HeroCard
            quote={quote}
            stage={stage}
            job={job}
            versionsCount={versionsCount}
            onStatusClick={() => setShowStatusSheet(true)}
            onLeadClick={openLeadPicker}
            onScheduleEstimate={() => setShowEstimateSheet(true)}
            onLogActivity={() => setShowActivitySheet(true)}
            onViewComms={() => navigate(`/moves/${quoteId}/communications`)}
            onLogIssue={() => setShowIssueModal(true)}
            onStartJob={handleStartJob}
            startingJob={startingJob}
            onViewSignOff={() => setShowSignOffDetail(true)}
            onAdvance={() => setConfirmAdvance(true)}
            onRevert={() => setConfirmRevert(true)}
            onRequestSignOff={handleRequestSignOff}
            onRequestPayment={() => setShowPaymentSheet(true)}
            advancing={advancing}
            reverting={reverting}
            updateQuote={updateQuote}
            navigate={navigate}
            trucks={fleetTrucks}
            warehouses={warehouses}
            onCheckOutTruck={handleCheckOutTruck}
            onCheckInTruck={handleCheckInTruck}
          />
        </div>

        {/* Mobile nav — for lead stage, nav cards appear after editable cards below */}
        {stage !== 'lead' && (
          <nav className="flex flex-col gap-3">
            {mobileNavItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `w-full text-left bg-white border rounded-xl p-4 shadow-brand-sm
                   flex items-center gap-4
                   transition-all duration-150
                   ${isActive
                     ? 'border-plum/40 shadow-brand-md'
                     : 'border-stone hover:border-plum/40 hover:shadow-brand-md active:bg-mist'
                   }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                      ${isActive ? 'bg-plum/10' : 'bg-mist'}`}
                    >
                      <span className={isActive ? 'text-plum' : 'text-slate'}>{item.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-small ${isActive ? 'text-plum' : 'text-midnight'}`}>{item.label}</p>
                    </div>
                    <ChevronRight size={18} className="shrink-0 text-slate" />
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Delete move — intentionally subtle (non-lead stages) */}
        {stage !== 'lead' && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 text-[12px] text-slate/60 hover:text-rose
                         transition-colors duration-150"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Detail cards — lead stage shows editable cards; quote/job show read-only on desktop only */}
      {stage === 'lead' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
          <EditableCustomerCard quote={quote} save={save} />
          <EditableCompanyCard quote={quote} save={save} org={orgSettings} />
          <EditableLeadInfoCard quote={quote} save={save} />
          <EditableMoveInfoCard quote={quote} save={save} />
          <EditableAddressesCard quote={quote} save={save} calculatingTransit={calculatingTransit} transit={transit} org={orgSettings} warehouseDistance={warehouseDistance} />
          <EditablePropertyDetailsCard quote={quote} save={save} orgId={orgId} />
          <EditableNotesCard quote={quote} save={save} />
        </div>
      )}

      {/* Lead stage: History nav card + delete at the bottom on mobile */}
      {stage === 'lead' && (
        <div className="md:hidden mt-4">
          <nav className="flex flex-col gap-3">
            {mobileNavItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `w-full text-left bg-white border rounded-xl p-4 shadow-brand-sm
                   flex items-center gap-4
                   transition-all duration-150
                   ${isActive
                     ? 'border-plum/40 shadow-brand-md'
                     : 'border-stone hover:border-plum/40 hover:shadow-brand-md active:bg-mist'
                   }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                      ${isActive ? 'bg-plum/10' : 'bg-mist'}`}
                    >
                      <span className={isActive ? 'text-plum' : 'text-slate'}>{item.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-small ${isActive ? 'text-plum' : 'text-midnight'}`}>{item.label}</p>
                    </div>
                    <ChevronRight size={18} className="shrink-0 text-slate" />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 text-[12px] text-slate/60 hover:text-rose
                         transition-colors duration-150"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
        </div>
      )}

      {stage !== 'lead' && cards && (
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 md:mt-0">
          {cards}
        </div>
      )}

      {/* Mobile sheets (desktop uses the shell's sheets) */}
      <BottomSheet open={showStatusSheet} onClose={() => setShowStatusSheet(false)}
        title={`Change ${LIFECYCLE_STAGE_LABEL[stage]} Status`}>
        <StatusSheet
          stage={stage}
          currentStatus={currentStatus}
          onSelect={handleStatusChange}
          onClose={() => setShowStatusSheet(false)}
        />
      </BottomSheet>

      <BottomSheet open={showUserPicker} onClose={() => setShowUserPicker(false)}
        title={userPickerMode === 'moveLead' ? 'Assign Move Lead' : 'Change Quote Lead'}>
        <UserPickerSheet
          users={pickerUsers}
          currentUid={currentLeadUid}
          onSelect={handleLeadAssign}
          onClose={() => setShowUserPicker(false)}
        />
      </BottomSheet>

      {showEstimateSheet && (
        <EstimateScheduledSheet
          users={orgUsers}
          usersLoading={usersLoading}
          onConfirm={handleEstimateScheduled}
          onClose={() => setShowEstimateSheet(false)}
        />
      )}

      {showActivitySheet && (
        <CommunicationLogSheet
          quote={quote}
          updateQuote={updateQuote}
          onClose={() => setShowActivitySheet(false)}
        />
      )}

      {/* Log Issue modal (job stage) */}
      <IssueLogModal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        onSubmit={async (data: IssueFormData) => {
          await logJobIssue(data);
          setShowIssueModal(false);
        }}
      />

      {/* Delete confirmation sheet */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
          <div className="absolute inset-0 bg-midnight/50" onClick={deletingQuote ? undefined : () => setShowDeleteConfirm(false)} aria-hidden="true" />
          <div
            className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10
                       md:rounded-2xl md:max-w-sm md:w-full md:pb-6"
          >
            <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-h3 text-midnight">Delete</h2>
              {!deletingQuote && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                             hover:text-midnight hover:bg-mist transition-colors"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <p className="text-[13px] text-slate leading-snug mb-5">
              Are you sure you want to delete the move for{' '}
              <span className="font-medium text-midnight">{quote?.customerCompanyName ?? 'this customer'}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingQuote}
                className="flex-1 h-11 rounded-xl border border-stone text-[13px] font-medium text-midnight
                           hover:bg-mist active:bg-stone/60 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteQuote}
                disabled={deletingQuote}
                className="flex-1 h-11 rounded-xl bg-rose text-[13px] font-medium text-white
                           hover:bg-rose/90 active:opacity-80 transition-colors disabled:opacity-50"
              >
                {deletingQuote ? 'Deleting\u2026' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Job Progress Modals ─────────────────────────────────────── */}

      {/* Advance confirmation */}
      <ConfirmModal
        isOpen={confirmAdvance}
        title={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Advance Stage') : 'Advance Stage'}
        message={`Are you sure you want to proceed to "${
          job?.jobStage
            ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) + 1] ?? 'complete']
            : 'next stage'
        }"?`}
        confirmLabel={job?.jobStage ? (JOB_STAGE_ACTION[job.jobStage] ?? 'Continue') : 'Continue'}
        variant="primary"
        onConfirm={handleAdvance}
        onClose={() => setConfirmAdvance(false)}
      />

      {/* Revert confirmation */}
      <ConfirmModal
        isOpen={confirmRevert}
        title="Revert Stage"
        message={`Are you sure you want to go back to "${
          job?.jobStage
            ? JOB_STAGE_LABEL[JOB_STAGES[JOB_STAGES.indexOf(job.jobStage) - 1] ?? 'scheduled']
            : 'previous stage'
        }"?`}
        confirmLabel="Revert"
        variant="danger"
        onConfirm={handleRevert}
        onClose={() => setConfirmRevert(false)}
      />

      {/* Sign-Off Capture Modal */}
      <SignOffCaptureModal
        isOpen={showSignOffCapture}
        onConfirm={handleSignOffCapture}
        onWaive={() => {
          setShowSignOffCapture(false);
          setShowWaiveSignOff(true);
        }}
        onClose={() => setShowSignOffCapture(false)}
        submitting={signOffSubmitting}
        error={signOffError}
      />

      {/* Waive Sign-Off Modal */}
      <WaiveSignOffModal
        isOpen={showWaiveSignOff}
        onConfirm={handleSignOffWaive}
        onClose={() => setShowWaiveSignOff(false)}
        submitting={signOffSubmitting}
      />

      {/* Sign-Off Detail Modal */}
      {job?.customerSignOff && (
        <SignOffDetailModal
          isOpen={showSignOffDetail}
          signOff={job.customerSignOff}
          onCaptureSignOff={() => {
            setShowSignOffDetail(false);
            setShowSignOffCapture(true);
          }}
          onClose={() => setShowSignOffDetail(false)}
        />
      )}

      {/* Payment Request Sheet */}
      {showPaymentSheet && job && orgId && quoteId && (
        <RequestPaymentSheet
          job={job}
          orgId={orgId}
          quoteId={quoteId}
          onSend={handleSendPaymentRequest}
          sending={paymentSending}
          onClose={() => setShowPaymentSheet(false)}
          onReconciled={handleReconciled}
        />
      )}
    </div>
  );
}
